import { useRef, useState, useMemo, useEffect, useCallback, type ChangeEvent } from 'react'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Tabs,
  TabList,
  Tab,
  Badge,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { Search, LayoutList, User, UserX, Trash2, UserPlus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { ActionButtons } from '@/components/common/ActionButtons'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { StatusPill } from '@/components/helpdesk/Cell/StatusPill'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAccessCheck } from '@/hooks/useAccess'
import { useAuthStore } from '@/store/auth'
import { useParentTicketStore } from '@/store/parentTicket'
import { useTicketViewStore } from '@/store/ticketView'
import { TicketService } from '@/lib/services/ticket'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { isTeamLead } from '@/lib/utils/roleHelper'

const MEMBERS_API_PATH = '/access-control/users/members'

interface AvailableAgentRow {
  id?: number
  first_name?: string
  last_name?: string
  user_login?: string
  assigned_tickets_count?: number
}

type TabValue = 'all' | 'my' | 'unassigned' | 'trash'
type FilterOption = { value: string | number; label: string }

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function ParentTicketsListPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const [searchQuery, setSearchQuery] = useState('')
  const [clientOptions, setClientOptions] = useState<FilterOption[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [totalMy, setTotalMy] = useState(0)
  const [totalUnassigned, setTotalUnassigned] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [modalMessage, setModalMessage] = useState('')
  const [assignMemberList, setAssignMemberList] = useState<AvailableAgentRow[]>([])
  const [assigningId, setAssigningId] = useState<number | null>(null)

  const ticketView = useTicketViewStore((s) => s.view)
  const setTicketView = useTicketViewStore((s) => s.setView)
  const user = useAuthStore((s) => s.user)
  const userIsTeamLead = isTeamLead(user)

  // Reset view to 'all' if current view is 'unassigned' but user is not a team lead
  useEffect(() => {
    if (ticketView === 'unassigned' && !userIsTeamLead) {
      setTicketView('all')
    }
  }, [ticketView, userIsTeamLead, setTicketView])

  // Load client options for filter
  useEffect(() => {
    const loadClients = async () => {
      try {
        const res = await apiClient.get('/options/clients')
        const clients = Array.isArray(res.data) ? res.data : []
        setClientOptions(
          clients.map((c: { id: number; name?: string; code?: string }) => ({
            value: String(c.id),
            label: c.name || c.code || String(c.id),
          }))
        )
      } catch {
        setClientOptions([])
      }
    }
    loadClients()
  }, [])

  // Load assignable agents when team lead (for Unassigned tab Assign to action)
  useEffect(() => {
    if (!userIsTeamLead) return
    const loadMembers = async () => {
      try {
        const res = await apiClient.get(MEMBERS_API_PATH, {
          params: { per_page: 10, order: 'assigned_tickets_count', sort: 'asc' },
        })
        const data = res.data?.data ?? res.data ?? []
        setAssignMemberList(Array.isArray(data) ? data : [])
      } catch {
        setAssignMemberList([])
      }
    }
    loadMembers()
  }, [userIsTeamLead])

  const access = useAccessCheck()

  const handleAssignParentTicket = useCallback(
    async (row: Record<string, unknown>, userId: number | null) => {
      const id = row.id != null ? String(row.id) : null
      if (!id) return
      setAssigningId(Number(id))
      try {
        await apiClient.patch(`/tickets/${id}`, { agentId: userId ?? null })
        showSuccess('Success', userId ? 'Ticket assigned successfully' : 'Ticket unassigned')
        tableRef.current?.reload()
        const res = await apiClient.get(MEMBERS_API_PATH, {
          params: { per_page: 10, order: 'assigned_tickets_count', sort: 'asc' },
        })
        const data = res.data?.data ?? res.data ?? []
        setAssignMemberList(Array.isArray(data) ? data : [])
      } catch (err) {
        showError(err as Error)
      } finally {
        setAssigningId(null)
      }
    },
    [showSuccess, showError]
  )

  const ticketViewTabs = useMemo(
    (): Array<{ key: TabValue; label: string; icon: typeof LayoutList }> => [
      { key: 'all', label: 'All Tickets', icon: LayoutList },
      { key: 'my', label: 'My Tickets', icon: User },
      ...(userIsTeamLead ? [{ key: 'unassigned' as TabValue, label: 'Unassigned', icon: UserX }] : []),
      { key: 'trash', label: 'Trash', icon: Trash2 },
    ],
    [userIsTeamLead]
  )

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      showActionsColumn: true,
      dataFields: {
        ticket_number: {
          name: 'Ticket ID',
          withSort: true,
          fullWidth: true,
        },
        request_number: {
          name: 'Request ID',
          withSort: false,
          maxWidth: '140px',
        },
        client_id: {
          name: 'Client',
          withSort: false,
          withFilter: true,
          filterOptions: clientOptions,
          maxWidth: '220px',
        },
        ...(ticketView !== 'my'
          ? {
              agent_name: {
                name: 'Agent',
                withSort: false,
                noTruncate: true,
              },
            }
          : {}),
        submitted_at: {
          name: 'Requested Date',
          withSort: false,
          noTruncate: true,
        },
        ticket_submitted_at: {
          name: 'Submitted at',
          withSort: false,
          noTruncate: true,
        },
        ageing: {
          name: 'Ageing',
          withSort: false,
          maxWidth: '90px',
        },
        severity: {
          name: 'Severity',
          withSort: true,
          maxWidth: '100px',
        },
        status: {
          name: 'Status',
          withSort: true,
          maxWidth: '200px',
        },
      },
      dataSource: '/tickets',
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: 'No parent tickets found',
      hideSelection: false,
      otherActions: [
        {
          name: 'View Details',
          onClick: (row) => {
            if (row?.id) {
              navigate(`/tickets/${row.id}/view`)
            }
          },
          show: (row) => {
            // Show "View Details" only when submitted (same as getRowPermissions: no edit/delete when ticket_submitted_at is set)
            return Boolean(row?.id && row?.ticket_submitted_at != null)
          },
        },
      ],
    }),
    [navigate, clientOptions, ticketView, userIsTeamLead, assignMemberList, assigningId, handleAssignParentTicket]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit, // Use otherActions "Edit" only (navigates to /tickets/:id/edit)
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access]
  )

  const tableParams = useMemo(() => {
    const params: Record<string, string | number | undefined> = {
      search: searchQuery || undefined,
    }
    if (ticketView === 'my' && user?.id) {
      params.assigned_to = user.id
    }
    if (ticketView === 'unassigned') {
      params.unassigned = 1
    }
    if (ticketView === 'trash') {
      params.trash = 1
    }
    return params
  }, [searchQuery, ticketView, user?.id])

  const setSubSub = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const setTickets = useParentTicketStore((s) => s.setTickets)
  const onDataLoad = useMemo(
    () => (data: unknown[], meta?: { all?: number; trashed?: number; my?: number; unassigned?: number }) => {
      if (meta?.all != null) setTotalRows(meta.all)
      if (meta?.trashed != null) setTotalTrash(meta.trashed)
      if (meta?.my != null) setTotalMy(meta.my)
      if (meta?.unassigned != null) setTotalUnassigned(meta.unassigned)
      const rows = Array.isArray(data) ? data : []
      const mapped = rows
        .map((row) => TicketService.mapApiRowToParentTicket((row as Record<string, unknown>) ?? {}))
        .filter((t) => t.id != null && t.id !== '')
      if (mapped.length) setTickets(mapped)
    },
    [setTickets]
  )

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }
    if (bulkAction === 'delete' && ticketView === 'trash') {
      setModalMessage('You are about to permanently delete these tickets. This action cannot be undone.')
    } else {
      setModalMessage('Are you sure you want to apply this action?')
    }
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    // For non-trash view, backend now skips submitted tickets; warn user if they selected any
    if (ticketView !== 'trash') {
      showWarning(
        'Submitted tickets will be skipped when applying bulk actions. Only draft tickets will be moved to trash.'
      )
    }

    setIsBulkProcessing(true)
    try {
      let url = ''
      let successMessage = 'Action completed successfully'

      if (bulkAction === 'restore') {
        url = '/tickets/bulk/restore'
        successMessage = 'Tickets restored successfully'
      } else if (bulkAction === 'delete') {
        url = ticketView === 'trash' ? '/tickets/bulk/force-delete' : '/tickets/bulk/delete'
        successMessage =
          ticketView === 'trash' ? 'Tickets permanently deleted' : 'Tickets moved to trash'
      }

      await apiClient.post(url, { ids: selectedIds })
      showSuccess('Success', successMessage)
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (error: unknown) {
      showError(error as Error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  const getTabIndex = () => {
    const index = ticketViewTabs.findIndex((tab) => tab.key === ticketView)
    return index >= 0 ? index : 0
  }

  const handleTabChange = (index: number) => {
    const selectedTab = ticketViewTabs[index]
    if (selectedTab) {
      tableRef.current?.clearData()
      setTicketView(selectedTab.key)
      setSearchQuery('')
      tableRef.current?.applyFilter('search', '')
      tableRef.current?.clearPage()
    }
  }

  const handleSearch = () => {
    tableRef.current?.applyFilter('search', searchQuery || '')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  return (
    <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
      <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
        <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
          <CardHeader flexShrink={0} py={3}>
            <Heading size="md" mb={1}>
              Parent Tickets
            </Heading>
            <Text fontSize="sm" color={colors.text}>
              Manage parent tickets created from acknowledged requests
            </Text>
          </CardHeader>

          <CardHeader py={0} px={0} flexShrink={0}>
            <Box pt={2} pb={0}>
              <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                <Tabs index={getTabIndex()} onChange={handleTabChange} variant="enclosed">
                  <TabList borderBottom="none">
                    {ticketViewTabs.map(({ key, label, icon: Icon }) => (
                      <Tab key={key}>
                        <HStack spacing={2}>
                          <Icon size={16} />
                          <Text>{label}</Text>
                          {key === 'all' && (
                            <Badge ml={2} colorScheme="blue">
                              {totalRows}
                            </Badge>
                          )}
                          {key === 'my' && (
                            <Badge ml={2} colorScheme="teal">
                              {totalMy}
                            </Badge>
                          )}
                          {key === 'unassigned' && (
                            <Badge ml={2} colorScheme="orange">
                              {totalUnassigned}
                            </Badge>
                          )}
                          {key === 'trash' && (
                            <Badge ml={2} colorScheme="red">
                              {totalTrash}
                            </Badge>
                          )}
                        </HStack>
                      </Tab>
                    ))}
                  </TabList>
                </Tabs>
                <Box pb={2}>
                  <ActionButtons
                    pathname="/tickets"
                    createRoute="/tickets/create"
                    createLabel="Create New Parent Ticket"
                  />
                </Box>
              </HStack>
              <Box borderBottom="2px" borderColor={colors.border} mx={4} />
            </Box>
          </CardHeader>

          <CardHeader py={1} pt={3} pb={2} flexShrink={0} px={5}>
            <HStack spacing={4} flexWrap="wrap" justify="flex-start" align="center">
              {access.can_delete && (
                <HStack spacing={2} minW="250px" flexShrink={0}>
                  <Select
                    placeholder="Bulk actions"
                    size="sm"
                    value={bulkAction}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                      e.stopPropagation()
                      setBulkAction(e.target.value)
                    }}
                  >
                    {ticketView === 'trash' && <option value="restore">Restore</option>}
                    {ticketView === 'trash' && <option value="delete">Delete Permanently</option>}
                    {ticketView !== 'trash' && <option value="delete">Move to trash</option>}
                  </Select>
                  <Button size="sm" colorScheme="blue" onClick={handleBulkActionClick} isLoading={isBulkProcessing}>
                    Apply
                  </Button>
                </HStack>
              )}
              <Box flex="0 1 auto" minW="250px" maxW="400px">
                <InputGroup size="sm">
                  <Input
                    ref={searchRef}
                    placeholder="Search by ticket ID, request ID, subject..."
                    value={searchQuery}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    pr="2.5rem"
                  />
                  <InputRightElement width="2.5rem">
                    <IconButton
                      aria-label="Search"
                      icon={<Search size={16} />}
                      size="sm"
                      h="1.75rem"
                      w="1.75rem"
                      onClick={handleSearch}
                      variant="ghost"
                    />
                  </InputRightElement>
                </InputGroup>
              </Box>
            </HStack>
          </CardHeader>

          <CardBody
            p={0}
            pt={1}
            pb={4}
            mx={5}
            flex="1"
            overflow="hidden"
            display="flex"
            flexDirection="column"
            minH={0}
            position="relative"
          >
            <Box h="100%" display="flex" flexDirection="column" minH={0}>
              <DataTable
                ref={tableRef}
                options={tableOptions}
                params={tableParams}
                filterOptions={{ client_id: clientOptions }}
                setSubSub={setSubSub}
                onDataLoad={onDataLoad}
                onOpenModal={(id) => navigate(`/tickets/${id}/edit`)}
                access={permissions}
                getRowPermissions={(row) =>
                  row.ticket_submitted_at
                    ? { can_edit: false, can_delete: false }
                    : {}
                }
                customRenderers={{
                  ticket_number: (value) => {
                    const str = value ? String(value) : '–'
                    return (
                      <Text as="span" noOfLines={1} display="block" w="100%" minW={0}>
                        {str}
                      </Text>
                    )
                  },
                  submitted_at: (value) => (value ? formatDateTime(String(value)) : '-'),
                  ticket_submitted_at: (value) => (value ? formatDateTime(String(value)) : '-'),
                  client_id: (_value, row) => {
                    const str = (row && typeof row === 'object' && 'client' in row ? (row as { client?: string }).client : _value) ?? '–'
                    const display = str ? String(str) : '–'
                    return (
                      <Text as="span" noOfLines={1} display="block" w="100%" minW={0}>
                        {display}
                      </Text>
                    )
                  },
                  agent_name: (value, row) => {
                    const name = value ? String(value) : null
                    const agent = row && typeof row === 'object' && 'agent' in row ? (row as { agent?: { avatar_url?: string; photo_url?: string } }).agent : undefined
                    const avatarSrc = agent?.avatar_url ?? agent?.photo_url
                    const showAssignAction = ticketView === 'unassigned' && userIsTeamLead
                    const rowId = row && typeof row === 'object' && 'id' in row ? (row as { id?: number }).id : undefined
                    const assignedToId = row && typeof row === 'object' && 'assigned_to' in row ? (row as { assigned_to?: number }).assigned_to : undefined
                    const isAssigning = rowId != null && assigningId === rowId
                    if (!showAssignAction) {
                      if (!name) return <Text as="span" color="gray.500">–</Text>
                      return (
                        <HStack spacing={2} align="center" minW={0}>
                          <Avatar size="sm" name={name} src={avatarSrc} bg="blue.500" color="white" flexShrink={0} />
                          <Text noOfLines={1} fontSize="sm" display="block" w="100%" minW={0}>
                            {name}
                          </Text>
                        </HStack>
                      )
                    }
                    return (
                      <HStack w="full" justify="space-between" spacing={2} onClick={(e) => e.stopPropagation()}>
                        {name ? (
                          <HStack spacing={2} align="center" minW={0} flex="1">
                            <Avatar size="sm" name={name} src={avatarSrc} bg="blue.500" color="white" flexShrink={0} />
                            <Text noOfLines={1} fontSize="sm" display="block" minW={0}>
                              {name}
                            </Text>
                          </HStack>
                        ) : (
                          <Text as="span" color="gray.500" flex="1" minW={0}>–</Text>
                        )}
                        <Menu placement="bottom-end" strategy="fixed">
                          <Tooltip label="Assign to" placement="top" hasArrow>
                            <MenuButton
                              as={IconButton}
                              aria-label="Assign to"
                              icon={<UserPlus size={18} />}
                              variant="ghost"
                              size="sm"
                              colorScheme="gray"
                              isLoading={isAssigning}
                              isDisabled={isAssigning}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Tooltip>
                          <MenuList maxH="300px" overflowY="auto" minW="220px" onClick={(e) => e.stopPropagation()}>
                            <MenuItem onClick={() => row && handleAssignParentTicket(row as Record<string, unknown>, null)}>
                              <Text fontSize="sm">Unassign</Text>
                            </MenuItem>
                            {user?.id != null && (
                              <MenuItem
                                onClick={() => row && handleAssignParentTicket(row as Record<string, unknown>, user.id)}
                                bg={assignedToId === user.id ? 'blue.50' : 'transparent'}
                              >
                                <Text fontSize="sm">Assign to me</Text>
                              </MenuItem>
                            )}
                            {assignMemberList.map((m) => {
                              const memberName = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.user_login || `User #${m.id}`
                              return (
                                <MenuItem
                                  key={m.id}
                                  onClick={() => row && handleAssignParentTicket(row as Record<string, unknown>, m.id ?? null)}
                                  bg={assignedToId === m.id ? 'blue.50' : 'transparent'}
                                >
                                  <Text fontSize="sm" noOfLines={1}>{memberName}</Text>
                                </MenuItem>
                              )
                            })}
                          </MenuList>
                        </Menu>
                      </HStack>
                    )
                  },
                  ageing: (value) => (value ? String(value) : '-'),
                  request_number: (value) => (value ? String(value) : '-'),
                  severity: (value) =>
                    value ? (
                      <StatusPill label={String(value)} variant="severity" />
                    ) : (
                      '-'
                    ),
                  status: (value, row) => {
                    const apiStatusLabel =
                      row && typeof row === 'object' && 'status_label' in row
                        ? (row as { status_label?: string }).status_label
                        : null
                    const apiStatusCode =
                      row && typeof row === 'object' && 'status' in row
                        ? (row as { status?: string }).status
                        : value
                    const label = apiStatusLabel || (apiStatusCode ? String(apiStatusCode) : '')
                    return label ? (
                      <StatusPill label={label} variant="status" />
                    ) : (
                      '-'
                    )
                  },
                }}
              />
            </Box>
          </CardBody>
        </Card>
      </VStack>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm</ModalHeader>
          <ModalBody>{modalMessage}</ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleConfirmBulkAction} isLoading={isBulkProcessing}>
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
